(ns manage.product-selector-page.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["react-hook-form" :as rhf]
            ["react-use/lib/useDebounce$default" :as useDebounce]
            [cljs-bean.core :refer [->clj ->js]]
            [common.modal.util :refer [with-confirmation-modal]]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.patterns.loading-view :refer [loading-view]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.product-selector :as product-selector.q]
            [common.widgets.overlay-page :as overlay-page]
            [common.widgets.page :as page]
            [manage.product-selector-page.hooks :as hooks]
            [manage.product-selector-page.sections.combination-strategy :refer [combination-strategy]]
            [manage.product-selector-page.sections.conditions :refer [conditions]]
            [manage.product-selector-page.sections.details :refer [details]]
            [manage.product-selector-page.sections.exclusion :refer [exclusion]]
            [manage.product-selector-page.sections.key-matching :refer [key-matching]]
            [manage.product-selector-page.sections.prioritisation :refer [prioritisation]]
            [manage.product-selector-page.sections.product-selectors :refer [product-selectors]]
            [manage.product-selector-page.sections.required-fields :refer [required-fields]]
            [manage.product-selector-page.sections.sorting :refer [sorting]]
            [manage.product-selector-result-table.view :as result-table]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defmodal confirm-delete ::exit-creation
  [{:keys [on-submit]}]
  ($ modal-v2
     {:title "Exit product selector creation?"
      :footer ($ Stack {:direction "row" :gap 1}
                 ($ Button
                    {:variant "text"
                     :sx #js {:color "text.primary"}
                     :onClick #(rf/dispatch [:modal/close])}
                    "Cancel")
                 ($ Button
                    {:variant "contained"
                     :color "error"
                     :onClick #(do (rf/dispatch [:modal/close])
                                   (on-submit))}
                    "Yes, exit"))
      :dialog-content-props {:sx #js {:width "392px"}}}
     "If you cancel now, any configurations or selections
      you’ve made will be lost. Are you sure you want to exit?"))

(defui create-buttons [{:keys [on-close submitting?]}]
  ($ Stack
     {:direction "row"
      :gap 1}
     ($ Button
        {:onClick on-close}
        "Cancel")
     ($ Button
        {:key "submit"
         :variant "contained"
         :type "submit"
         :disabled submitting?}
        "Save")))

(defui edit-buttons [{:keys [submitting? on-edit-cancel]}]
  ($ Stack
     {:direction "row"
      :gap 1}
     ($ Button
        {:onClick on-edit-cancel}
        "Cancel")
     ($ Button
        {:key "submit"
         :variant "contained"
         :type "submit"
         :disabled submitting?}
        "Save")))

(defui read-buttons [{:keys [set-mode product-selector-id]}]
  (let [{:keys [advertiser-id] :as url-context} (use-subscribe [:url-context])
        {delete-product-selector :mutate}
        (use-mutation (product-selector.q/delete-product-selector-mutation
                       {:advertiser-id advertiser-id}))

        on-delete (uix/use-callback
                   (fn []
                     (delete-product-selector
                      {:id product-selector-id}
                      #js {:onSuccess #(r.easy/navigate :advertiser/products {:path-params url-context
                                                                              :fragment "product-selector"})}))
                   [delete-product-selector url-context product-selector-id])]
    ($ Stack
       {:direction "row"
        :gap 1}
       ($ Button
          {:onClick #(rf/dispatch
                      [:modal/open {:id :product-selector/confirm-delete
                                    :props {:on-confirm on-delete}}])
           :color "error"}
          "Delete")
       ($ Button
          {:variant "contained"
           :onClick #(set-mode :edit)}
          "Edit"))))

(defui buttons [{:keys [mode] :& props}]
  (case mode
    :create ($ create-buttons props)
    :edit ($ edit-buttons props)
    :read ($ read-buttons props)))

(defui product-selector-form [{:keys [product-selector]}]
  (let [{:keys [on-submit mode set-mode on-edit-cancel]
         {:keys [control ^js formState] :as form-control} :form-control}
        (hooks/use-product-selector-form product-selector)

        form-values (rhf/useWatch #js {:control control})
        [debounced-form-values set-debounced-form-values] (uix/use-state {})

        on-close (uix/use-callback
                  (fn []
                    (with-confirmation-modal (= mode :create)
                      ::exit-creation
                      {}
                      #(.back js/history)))
                  [mode])]
    (useDebounce (fn []
                   (set-debounced-form-values (->clj form-values)))
                 500
                 #js [form-values])
    ($ rhf/FormProvider (->js (merge form-control {:mode mode}))
       ($ Stack
          {:component "form"
           :onSubmit on-submit
           :sx #js {:overflow "hidden"}}
          ($ overlay-page/header
             {:title "Product selector"
              :on-close on-close}
             ($ buttons
                {:mode mode
                 :set-mode set-mode
                 :on-close on-close
                 :on-edit-cancel on-edit-cancel
                 :submitting? (.-isSubmitting formState)
                 :product-selector-id (:id product-selector)}))
          ($ page/content-padding
             {:direction "row"
              :gap 3
              :sx #js {:overflowY "auto"
                       :pt 3}}
             ($ Stack
                {:gap 3
                 :sx #js {:width "480px"}}
                ($ details)
                (case (.-type form-values)
                  "single" ($ :<>
                              ($ conditions)
                              ($ sorting)
                              ($ key-matching)
                              ($ required-fields)
                              ($ prioritisation)
                              ($ exclusion))
                  "combined" ($ :<>
                                ($ product-selectors)
                                ($ combination-strategy))))
             ($ Paper
                {:sx #js {:position "sticky"
                          :top 0
                          :padding 3
                          :flex 1
                          :alignSelf "flex-start"}}
                ($ result-table/product-selector-result-datagrid
                   {:product-selector debounced-form-values})))))))

(def product-selector-page
  (uix/as-react
   (fn [{:keys [ref style]}]
     (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
           {:keys [product-selector-id]} (use-subscribe [:router/query-parameters])
           {product-selector :data isSuccess :isSuccess} (use-query (product-selector.q/product-selector-query
                                                                     {:advertiser-id advertiser-id
                                                                      :product-selector-id product-selector-id}))]
       ($ overlay-page/overlay-page
          {:ref ref
           :style style}
          (if (or (nil? product-selector-id) isSuccess)
            ($ product-selector-form
               {:product-selector product-selector})
            ($ loading-view)))))))
